import { FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import { Input, Label, Button, Select } from "../../common";
import { FormErrorMessage } from "../../messages";
import { PHONE_CHANNEL_OPTIONS } from "../../../constants";
import { ROUTES } from "../../../routes";
import { useUpdatePhoneMutation } from "../../../store/services/user";
import { PhoneChannel } from "../../../types/UserTypes";

type Inputs = {
  phone: string;
  channel: PhoneChannel;
};

export const AddNumberForm: FC = () => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
  } = useForm<Inputs>({
    defaultValues: {
      phone: sessionStorage.getItem("phoneNumber") || "",
      channel: PhoneChannel.SMS,
    },
  });

  const phoneValue = watch("phone");
  const phoneChannel = watch("channel");
  const navigate = useNavigate();

  const [updatePhone, { isSuccess, isLoading, isError, error }] =
    useUpdatePhoneMutation();

  const onSubmit: SubmitHandler<Inputs> = async (data) =>
    await updatePhone(data);

  useEffect(() => {
    if (isSuccess) {
      sessionStorage.setItem("phoneNumber", phoneValue);
      sessionStorage.setItem("phoneChannel", phoneChannel);
      navigate(ROUTES.AUTH_CONFIRM_NUMBER);
    }
  }, [isLoading, isSuccess, navigate, phoneValue, phoneChannel]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
      <Label label="Phone number">
        <Controller
          name="phone"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              type="text"
              note="For example: +11234567890"
              autoFocus
              {...field}
            />
          )}
        />
      </Label>

      <Label label="Channel">
        <Controller
          name="channel"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select {...field} options={PHONE_CHANNEL_OPTIONS} />
          )}
        />
      </Label>

      <FormErrorMessage isError={isError} error={error} />

      <Button
        type="submit"
        variant={Button.variant.PRIMARY}
        className="w-full"
        disabled={!isValid}
        data-test="send-sms-btn"
      >
        Send Code
      </Button>

      {phoneChannel === PhoneChannel.SMS && (
        <div className="text-sm	text-gray-300 text-center">
          SMS can take several minutes to arrive in some countries.
        </div>
      )}

      <div className="text-sm	text-gray-300 text-center">
        We send only important notifications regarding invoices. No spam.
      </div>
    </form>
  );
};
